import React, { useState, useEffect, useCallback } from 'react';
import { companyLicensesAPI } from '../lib/api';
import RequireCap, { useCap } from '../components/auth/RequireCap';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, Search, FileBadge, Pencil, Trash2, Eye, Upload, Download, FileSpreadsheet,
  History, Paperclip, AlertTriangle, X,
} from 'lucide-react';

const STATUSES = ['Active', 'Pending', 'Expired', 'Inactive', 'Not renewing'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const emptyLicense = {
  license_type: '', state: '', city_county: '', license_number: '',
  issuing_agency: '', issue_date: '', renewal_date: '', status: 'Active',
  owner: '', notes: '', alert_recipients: '',
};

const statusTone = (s) => ({
  Active: 'default', Pending: 'secondary', Expired: 'destructive',
  Inactive: 'outline', 'Not renewing': 'outline',
}[s] || 'secondary');

function daysBadge(renewalDate) {
  if (!renewalDate) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(renewalDate + 'T00:00:00');
  const days = Math.round((exp - today) / 86400000);
  if (Number.isNaN(days)) return null;
  if (days < 0) return { text: `Expired ${Math.abs(days)}d ago`, cls: 'text-red-600' };
  if (days <= 30) return { text: `${days}d left`, cls: 'text-amber-600' };
  if (days <= 90) return { text: `${days}d left`, cls: 'text-slate-500' };
  return { text: `${days}d left`, cls: 'text-slate-400' };
}

function triggerBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
}

export default function CompanyLicenses() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole && hasRole('admin');
  const canAdd = useCap('compliance_licenses', 'add');
  const canEdit = useCap('compliance_licenses', 'edit');
  const canExport = useCap('compliance_licenses', 'export');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyLicense });
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 500 };
      if (search.trim()) params.q = search.trim();
      if (stateFilter !== 'all') params.state = stateFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await companyLicensesAPI.list(params);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.userMessage || 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, [search, stateFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyLicense });
    setShowForm(true);
  };

  const openEdit = (lic) => {
    setEditingId(lic.id);
    setForm({
      ...emptyLicense,
      ...Object.fromEntries(Object.entries(emptyLicense).map(([k]) => [k, lic[k] ?? ''])),
      alert_recipients: (lic.alert_recipients || []).join(', '),
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.license_type.trim() || !form.state.trim() || !form.license_number.trim()) {
      toast.error('License Type, State and License Number are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        state: form.state.toUpperCase(),
        city_county: form.city_county || null,
        issuing_agency: form.issuing_agency || null,
        issue_date: form.issue_date || null,
        renewal_date: form.renewal_date || null,
        owner: form.owner || null,
        notes: form.notes || null,
        alert_recipients: form.alert_recipients
          ? form.alert_recipients.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      if (editingId) {
        await companyLicensesAPI.update(editingId, payload);
        toast.success('License updated');
      } else {
        await companyLicensesAPI.create(payload);
        toast.success('License added');
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.userMessage || 'Failed to save license');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lic) => {
    if (!window.confirm(`Delete ${lic.license_type} #${lic.license_number} (${lic.state})? This cannot be undone.`)) return;
    try {
      await companyLicensesAPI.delete(lic.id);
      toast.success('License deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const doExport = async () => {
    try {
      const { data } = await companyLicensesAPI.export();
      triggerBlob(data, 'company_licenses.xlsx');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const doTemplate = async () => {
    try {
      const { data } = await companyLicensesAPI.template();
      triggerBlob(data, 'company_licenses_template.xlsx');
    } catch (e) {
      toast.error('Template download failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="company-licenses-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Company Licenses</h1>
          <p className="text-slate-500 mt-1">Track the company's operating licenses and renewal alerts</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <RequireCap module="compliance_licenses" action="export">
            <Button variant="outline" onClick={doExport} data-testid="licenses-export-btn">
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
          </RequireCap>
          <RequireCap module="compliance_licenses" action="add">
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="licenses-import-btn">
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
          </RequireCap>
          <RequireCap module="compliance_licenses" action="add">
            <Button onClick={openCreate} data-testid="add-license-btn">
              <Plus className="w-4 h-4 mr-2" /> Add License
            </Button>
          </RequireCap>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search type, number, agency, owner..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="licenses-search"
            />
          </div>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-full md:w-[160px]" data-testid="licenses-state-filter">
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All states</SelectItem>
              {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[160px]" data-testid="licenses-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        {loading ? (
          <div className="p-8 space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-14 skeleton-pulse rounded" />)}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>License</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Number</TableHead>
                <TableHead>Renewal</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(lic => {
                const db = daysBadge(lic.renewal_date);
                return (
                  <TableRow key={lic.id} data-testid={`license-row-${lic.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                          <FileBadge className="w-4 h-4 text-emerald-600" />
                        </div>
                        <span className="font-medium">{lic.license_type}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600" data-testid={`license-location-${lic.id}`}>
                      {lic.city_county ? `${lic.city_county}, ${lic.state}` : lic.state}
                    </TableCell>
                    <TableCell className="font-mono text-sm" data-testid={`license-number-${lic.id}`}>{lic.license_number}</TableCell>
                    <TableCell className="text-sm">
                      {lic.renewal_date || <span className="text-slate-400">—</span>}
                      {db && <div className={`text-xs ${db.cls}`} data-testid={`license-days-${lic.id}`}>{db.text}</div>}
                    </TableCell>
                    <TableCell><Badge variant={statusTone(lic.status)}>{lic.status}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{lic.owner || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setViewing(lic)} title="View" data-testid={`view-license-${lic.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <RequireCap module="compliance_licenses" action="edit">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(lic)} title="Edit" data-testid={`edit-license-${lic.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </RequireCap>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(lic)} title="Delete (admin)" data-testid={`delete-license-${lic.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <FileBadge className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No company licenses found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>
      {!loading && (
        <p className="text-xs text-slate-400 text-right" data-testid="licenses-count">Showing {rows.length} of {total}</p>
      )}

      <LicenseFormDialog
        open={showForm} onOpenChange={setShowForm}
        form={form} setForm={setForm}
        editingId={editingId} saving={saving} onSave={handleSave}
      />

      {viewing && (
        <LicenseDetailDialog
          license={viewing}
          onClose={() => setViewing(null)}
          onChanged={load}
          canEdit={canEdit}
        />
      )}

      <ImportDialog
        open={showImport} onOpenChange={setShowImport}
        onTemplate={doTemplate} onDone={load}
      />
    </div>
  );
}

// ── Create / Edit dialog ─────────────────────────────────────────────────
function LicenseFormDialog({ open, onOpenChange, form, setForm, editingId, saving, onSave }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle data-testid="license-dialog-title">{editingId ? 'Edit License' : 'Add Company License'}</DialogTitle>
          <DialogDescription>Company operating license — security agency, business, occupational permit, etc.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>License Type *</Label>
              <Input value={form.license_type} onChange={(e) => set('license_type', e.target.value)} placeholder="Security Agency" data-testid="license-type-input" />
            </div>
            <div className="space-y-2">
              <Label>State *</Label>
              <Input value={form.state} onChange={(e) => set('state', e.target.value.toUpperCase())} maxLength={2} placeholder="TX" data-testid="license-state-input" />
            </div>
            <div className="space-y-2">
              <Label>City / County <span className="text-xs text-slate-400">(optional)</span></Label>
              <Input value={form.city_county} onChange={(e) => set('city_county', e.target.value)} placeholder="Dallas County" data-testid="license-city-input" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>License Number *</Label>
              <Input value={form.license_number} onChange={(e) => set('license_number', e.target.value)} placeholder="00123456 or A 1200283 / B 1200281" data-testid="license-number-input" />
              <p className="text-xs text-slate-500">Kept exactly as text — leading zeroes and multi-value numbers are preserved.</p>
            </div>
            <div className="space-y-2">
              <Label>Issuing Agency</Label>
              <Input value={form.issuing_agency} onChange={(e) => set('issuing_agency', e.target.value)} placeholder="Dept of Public Safety" data-testid="license-agency-input" />
            </div>
            <div className="space-y-2">
              <Label>Owner / Responsible</Label>
              <Input value={form.owner} onChange={(e) => set('owner', e.target.value)} placeholder="Compliance" data-testid="license-owner-input" />
            </div>
            <div className="space-y-2">
              <Label>Issue Date</Label>
              <Input type="date" value={form.issue_date} onChange={(e) => set('issue_date', e.target.value)} data-testid="license-issue-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Renewal / Expiration Date</Label>
              <Input type="date" value={form.renewal_date} onChange={(e) => set('renewal_date', e.target.value)} data-testid="license-renewal-date-input" />
              <p className="text-xs text-slate-500">Drives 90/60/30/7-day + expiry alerts.</p>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger data-testid="license-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alert Recipients <span className="text-xs text-slate-400">(comma-separated emails)</span></Label>
              <Input value={form.alert_recipients} onChange={(e) => set('alert_recipients', e.target.value)} placeholder="a@co.com, b@co.com" data-testid="license-recipients-input" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="license-notes-input" />
            </div>
          </div>
          <Button onClick={onSave} disabled={saving} className="w-full" data-testid="save-license-btn">
            {saving ? 'Saving...' : editingId ? 'Update License' : 'Create License'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog (history + documents) ───────────────────────────────────
function LicenseDetailDialog({ license, onClose, onChanged, canEdit }) {
  const [history, setHistory] = useState([]);
  const [docs, setDocs] = useState(license.documents || []);
  const [uploading, setUploading] = useState(false);

  const loadHistory = useCallback(() => {
    companyLicensesAPI.history(license.id).then(({ data }) => setHistory(data || [])).catch(() => setHistory([]));
  }, [license.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const refreshDocs = async () => {
    const { data } = await companyLicensesAPI.get(license.id);
    setDocs(data.documents || []);
    loadHistory();
    onChanged?.();
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await companyLicensesAPI.uploadDocument(license.id, fd);
      toast.success('Document uploaded');
      await refreshDocs();
    } catch (err) {
      toast.error(err.userMessage || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadDoc = async (d) => {
    try {
      const { data } = await companyLicensesAPI.downloadDocument(license.id, d.id);
      triggerBlob(data, d.filename);
    } catch {
      toast.error('Download failed');
    }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Remove "${d.filename}"?`)) return;
    try {
      await companyLicensesAPI.deleteDocument(license.id, d.id);
      toast.success('Document removed');
      await refreshDocs();
    } catch {
      toast.error('Remove failed');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="license-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileBadge className="w-5 h-5 text-emerald-600" />
            {license.license_type} <Badge variant={statusTone(license.status)}>{license.status}</Badge>
          </DialogTitle>
          <DialogDescription>
            #{license.license_number} · {license.city_county ? `${license.city_county}, ${license.state}` : license.state}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 max-h-[72vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Detail label="Issuing Agency" value={license.issuing_agency} />
            <Detail label="Owner" value={license.owner} />
            <Detail label="Issue Date" value={license.issue_date} />
            <Detail label="Renewal Date" value={license.renewal_date} />
          </div>
          {license.notes && <p className="text-sm text-slate-600 border-t pt-3">{license.notes}</p>}

          {/* Documents */}
          <section className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Paperclip className="w-4 h-4" /> Certificate Documents</p>
              {canEdit && (
                <label className="cursor-pointer">
                  <input type="file" className="hidden" onChange={onUpload} data-testid="license-doc-upload-input" />
                  <span className="inline-flex items-center text-sm px-3 py-1.5 rounded-md border hover:bg-slate-50">
                    <Upload className="w-4 h-4 mr-1" />{uploading ? 'Uploading…' : 'Upload'}
                  </span>
                </label>
              )}
            </div>
            <div className="space-y-2">
              {docs.map(d => (
                <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-2" data-testid={`license-doc-${d.id}`}>
                  <button className="text-indigo-600 hover:underline truncate" onClick={() => downloadDoc(d)}>{d.filename}</button>
                  {canEdit && (
                    <Button variant="ghost" size="icon" onClick={() => removeDoc(d)} data-testid={`delete-doc-${d.id}`}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
              {docs.length === 0 && <p className="text-sm text-slate-400">No documents attached.</p>}
            </div>
          </section>

          {/* History */}
          <section className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><History className="w-4 h-4" /> History & Audit</p>
            <div className="space-y-3">
              {history.map(h => (
                <div key={h.id} className="text-sm border-l-2 border-slate-200 pl-3" data-testid={`license-history-${h.id}`}>
                  <p className="font-medium capitalize">{String(h.action).replace(/_/g, ' ')}</p>
                  <p className="text-xs text-slate-400">{new Date(h.at).toLocaleString()} · {h.by_name}</p>
                  {(h.changes || []).map((c, i) => (
                    <p key={i} className="text-xs text-slate-500">{c.field}: <span className="line-through">{String(c.old ?? '—')}</span> → {String(c.new ?? '—')}</p>
                  ))}
                </div>
              ))}
              {history.length === 0 && <p className="text-sm text-slate-400">No history yet.</p>}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-slate-700">{value || '—'}</p>
    </div>
  );
}

// ── Import dialog (preview → confirm) ──────────────────────────────────────
function ImportDialog({ open, onOpenChange, onTemplate, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importError, setImportError] = useState('');
  const [applyUpdates, setApplyUpdates] = useState({});
  const [skip, setSkip] = useState({});

  const reset = () => { setPreview(null); setApplyUpdates({}); setSkip({}); setImportError(''); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setImportError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await companyLicensesAPI.importPreview(fd);
      setPreview(data);
      // Default: apply all update rows.
      const au = {};
      (data.rows || []).forEach(r => { if (r.action === 'update') au[r.row] = true; });
      setApplyUpdates(au);
    } catch (err) {
      const msg = err.response?.data?.detail || err.userMessage || 'Could not read the file';
      setImportError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const confirm = async () => {
    setBusy(true);
    setImportError('');
    try {
      const payload = {
        token: preview.token,
        apply_updates: Object.entries(applyUpdates).filter(([, v]) => v).map(([k]) => Number(k)),
        skip: Object.entries(skip).filter(([, v]) => v).map(([k]) => Number(k)),
      };
      const { data } = await companyLicensesAPI.importConfirm(payload);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated, ${data.skipped} skipped`);
      reset();
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.userMessage || 'Import failed';
      setImportError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl" data-testid="license-import-dialog">
        <DialogHeader>
          <DialogTitle>Import Company Licenses</DialogTitle>
          <DialogDescription>Upload an Excel file. Required columns: License Type, State, License Number.</DialogDescription>
        </DialogHeader>

        {importError && (
          <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="license-import-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{importError}</span>
          </div>
        )}

        {!preview ? (
          <div className="space-y-4 py-2">
            <Button variant="outline" onClick={onTemplate} data-testid="license-template-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Template
            </Button>
            <label className="block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-slate-50">
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="license-import-file-input" />
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-600">{busy ? 'Reading…' : 'Click to choose an Excel file'}</p>
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm" data-testid="license-import-summary">
              <span className="text-emerald-600">{preview.summary.new} new</span>
              <span className="text-amber-600">{preview.summary.update} updates</span>
              <span className="text-red-600">{preview.summary.error} errors</span>
              <span className="text-slate-500">{preview.summary.total} rows</span>
            </div>
            <div className="max-h-[50vh] overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Row</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Type / State / Number</TableHead>
                    <TableHead>Apply</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map(r => (
                    <TableRow key={r.row} data-testid={`import-row-${r.row}`}>
                      <TableCell>{r.row}</TableCell>
                      <TableCell>
                        <Badge className={r.action === 'update' ? 'bg-amber-500 text-white hover:bg-amber-500' : ''} variant={r.action === 'new' ? 'default' : r.action === 'update' ? 'default' : 'destructive'}>{r.action}</Badge>
                        {r.errors?.length > 0 && (
                          <div className="text-xs text-red-500 flex items-center gap-1 mt-1">
                            <AlertTriangle className="w-3 h-3" />{r.errors.join('; ')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.data.license_type} · {r.data.state} · <span className="font-mono">{r.data.license_number}</span>
                      </TableCell>
                      <TableCell>
                        {r.action === 'update' && (
                          <input
                            type="checkbox"
                            checked={!!applyUpdates[r.row]}
                            onChange={(e) => setApplyUpdates(a => ({ ...a, [r.row]: e.target.checked }))}
                            data-testid={`import-apply-${r.row}`}
                          />
                        )}
                        {r.action === 'new' && <span className="text-xs text-emerald-600">will add</span>}
                        {r.action === 'error' && <span className="text-xs text-slate-400">skipped</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset} data-testid="import-back-btn">Choose another file</Button>
              <Button onClick={confirm} disabled={busy} data-testid="import-confirm-btn">
                {busy ? 'Importing…' : 'Confirm Import'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
